import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Shield,
  Users,
  TrendingDown,
  Calculator,
  Building2,
  Fuel,
  Wine,
  UtensilsCrossed,
  Briefcase,
  CheckCircle2,
} from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import NextBestAction from "@/components/NextBestAction";
import usePageSEO from "@/hooks/usePageSEO";

const PAIN_POINTS = [
  {
    Icon: Users,
    title: "Losing your top income earner",
    body: "If a key person leaves, key-person coverage keeps the business running while you recover and reorganize.",
  },
  {
    Icon: Shield,
    title: "What happens to the business if you die",
    body: "Buy-sell agreements funded with life insurance keep ownership transitions clean — and the family paid fairly.",
  },
  {
    Icon: TrendingDown,
    title: "A bad quarter wipes out cash",
    body: "Business Overhead Expense coverage keeps rent, payroll, and lease payments flowing if you're sidelined by illness.",
  },
  {
    Icon: Calculator,
    title: "Retirement planning without a W-2 playbook",
    body: "SEP IRAs, Solo 401(k)s, non-qualified deferred comp, and IUL — we coordinate the right mix for owner compensation.",
  },
];

const VERTICALS = [
  {
    slug: "hospitality",
    Icon: Building2,
    title: "Hospitality",
    desc: "Hotels, motels, B&Bs",
    body: "SBA 7(a) loan protection, key-person on the GM, seasonal cash flow, partner buy-sell.",
  },
  {
    slug: "gas-station-convenience",
    Icon: Fuel,
    title: "Gas & C-Store",
    desc: "Gas stations, convenience stores",
    body: "Family business succession, multi-location coordination, owner life + DI + overhead.",
  },
  {
    slug: "liquor-retail",
    Icon: Wine,
    title: "Liquor & Specialty Retail",
    desc: "Liquor stores, specialty retail",
    body: "Inventory-heavy risk, cash-handling, license transfer, succession planning.",
  },
  {
    slug: "restaurant-food-service",
    Icon: UtensilsCrossed,
    title: "Restaurants & Food Service",
    desc: "Restaurants, food trucks, catering",
    body: "Owner-operator protection, personal guarantees on leases, equipment financing.",
  },
  {
    slug: "general",
    Icon: Briefcase,
    title: "General",
    desc: "Any small business owner",
    body: "Universal owner planning — life, disability, overhead, retirement, and succession.",
  },
];

const PROCESS = [
  { n: "01", title: "Discovery", body: "Understand the business, the owners, and where the risk actually lives." },
  { n: "02", title: "Risk Assessment", body: "Quantify what a death, disability, or departure would cost — in dollars." },
  { n: "03", title: "Structure", body: "Design the right buy-sell / key-person / overhead / retirement combination." },
  { n: "04", title: "Fund", body: "Source carriers, underwrite, and place the coverage at the right economics." },
  { n: "05", title: "Review", body: "Annual check-ins so the plan ages with the business, not against it." },
];

export default function BusinessOwners() {
  usePageSEO({
    title:
      "Small Business Owner Protection & Succession Planning | Lifespan Wealth Partners",
    description:
      "Independent business owner planning — key-person insurance, buy-sell agreements, business overhead expense coverage, and owner retirement structures. Hospitality, gas station, liquor retail, restaurants, and general small business.",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "FinancialProduct",
      name: "Business Owner Protection Planning",
      provider: { "@id": "https://lifespanwealthpartners.com/#org" },
      category: "Business Insurance & Succession Planning",
    },
  });

  return (
    <div data-testid="business-owners-page" className="min-h-screen bg-lw-ivory">
      <Nav />
      <main>
        {/* Hero */}
        <section className="relative pt-28 sm:pt-32 lg:pt-36 pb-16 hero-grain">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <span className="lw-eyebrow">For Small Business Owners</span>
              <h1
                className="mt-4 font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-lw-navy"
                data-testid="bo-hero-headline"
              >
                Protection built for business owners.{" "}
                <span className="italic text-lw-gold">
                  From gas station to generational wealth.
                </span>
              </h1>
              <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-lw-muted">
                Small business owners wear every hat — but most have never had a real
                conversation about what happens to the business if the owner can't
                show up tomorrow. We fix that, quietly and compliantly.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/apply?audience=business-owner" className="btn-gold">
                  Schedule a 15-min call <ArrowRight size={18} />
                </Link>
                <Link to="/tools/life-needs" className="btn-ghost-navy">
                  See my protection gap
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="relative rounded-[1.75rem] overflow-hidden border border-lw-gold/40 shadow-[0_30px_80px_-30px_rgba(10,37,64,0.35)] bg-lw-navy p-8">
                <div className="font-sans-ui text-[0.65rem] tracking-[0.22em] uppercase text-lw-gold mb-5">
                  Business value — with and without planning
                </div>
                <BusinessValueMini />
                <p className="mt-4 text-[0.7rem] text-[#FAF7F2]/60 leading-snug">
                  Illustrative only. Not a projection or guarantee.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Pain Points */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <span className="lw-eyebrow">What keeps you up at night</span>
              <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl text-lw-navy leading-tight">
                The four risks business owners{" "}
                <span className="italic">rarely plan for.</span>
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {PAIN_POINTS.map(({ Icon, title, body }, i) => (
                <motion.div
                  key={title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.45, delay: i * 0.08 }}
                  className="lw-card p-6"
                  data-testid={`bo-pain-${i}`}
                >
                  <div className="w-11 h-11 rounded-xl bg-lw-navy text-lw-gold flex items-center justify-center">
                    <Icon size={20} />
                  </div>
                  <h3 className="mt-5 font-serif-display text-lg text-lw-navy leading-snug">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-lw-muted">{body}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Verticals */}
        <section className="py-20 bg-lw-ivory-warm">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <span className="lw-eyebrow">Your Industry</span>
              <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl text-lw-navy leading-tight">
                Protection shaped around <span className="italic">your industry.</span>
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {VERTICALS.map(({ slug, Icon, title, desc, body }) => (
                <Link
                  key={slug}
                  to={`/for-business-owners/${slug}`}
                  data-testid={`bo-vertical-${slug}`}
                  className="lw-card group p-6 flex flex-col h-full"
                >
                  <div className="w-11 h-11 rounded-xl bg-lw-gold/15 text-[#7f6218] flex items-center justify-center">
                    <Icon size={20} />
                  </div>
                  <h3 className="mt-5 font-serif-display text-xl text-lw-navy leading-tight">{title}</h3>
                  <div className="mt-1 font-sans-ui text-[0.65rem] tracking-[0.22em] uppercase text-lw-gold">
                    {desc}
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-lw-muted flex-1">{body}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-lw-navy group-hover:text-[#7f6218] transition-colors">
                    Explore <ArrowRight size={14} />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <span className="lw-eyebrow">Our Process</span>
              <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl text-lw-navy leading-tight">
                Five steps. <span className="italic">No pitch deck.</span>
              </h2>
            </div>
            <ol className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {PROCESS.map(({ n, title, body }, i) => (
                <motion.li
                  key={n}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="rounded-xl border border-lw-navy/10 bg-lw-ivory-warm p-5"
                >
                  <span className="font-serif-display text-3xl text-lw-gold">{n}</span>
                  <h3 className="mt-3 font-serif-display text-base text-lw-navy leading-snug">{title}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-lw-muted">{body}</p>
                </motion.li>
              ))}
            </ol>
          </div>
        </section>

        {/* CTA band */}
        <section className="py-20 gradient-navy-indigo relative overflow-hidden">
          <div
            aria-hidden="true"
            className="absolute -top-20 -right-20 w-[400px] h-[400px] rounded-full"
            style={{
              background: "radial-gradient(circle, rgba(212,175,55,0.22) 0%, rgba(212,175,55,0) 70%)",
            }}
          />
          <div className="relative max-w-3xl mx-auto px-5 sm:px-8 text-center">
            <h2 className="font-serif-display text-3xl sm:text-4xl leading-tight text-white">
              Ready to pressure-test your business plan?
            </h2>
            <p className="mt-4 text-base text-[#FAF7F2]/80 leading-relaxed">
              Fifteen minutes. One call. Walk away with clear answers and no
              commitment.
            </p>
            <div className="mt-8 flex justify-center flex-wrap gap-3">
              <Link
                to="/apply?audience=business-owner"
                data-testid="bo-cta-schedule"
                className="btn-gold"
              >
                Schedule your call <ArrowRight size={18} />
              </Link>
              <Link
                to="/learn?tag=business-owners"
                data-testid="bo-cta-learn"
                className="btn-ghost-light"
              >
                Read articles for owners
              </Link>
            </div>
          </div>
        </section>

        {/* Small compliance footer */}
        <section className="py-14 bg-lw-ivory-warm">
          <div className="max-w-4xl mx-auto px-5 text-xs text-lw-muted leading-relaxed">
            Business insurance, life insurance, disability income, and buy-sell
            funding are sold through licensed insurance producers. Product
            availability, underwriting outcomes, and specific features vary by
            carrier and state. This page is educational — not tax, legal, or
            accounting advice. Coordinate with your CPA and attorney when
            structuring entities, buy-sell agreements, and succession plans.
          </div>
        </section>

        <NextBestAction
          pageId="business-owners"
          step={2}
          heading="Pick your industry to get specifics"
          rationale="Buy-sell funding, key-person coverage, and 162 plans look different for restaurant owners vs. medical practices. Pick your industry and we'll show the exact playbook."
          primary={{ label: "Pick your industry", to: "#verticals", testid: "nba-bo-primary" }}
          secondary={{ label: "Schedule a strategy call", to: "/apply?source=business-owners", testid: "nba-bo-secondary" }}
        />
      </main>
      <Footer />
    </div>
  );
}

/* Small inline SVG chart — "with plan" vs "without plan" business value. */
function BusinessValueMini() {
  return (
    <svg viewBox="0 0 400 180" className="w-full h-auto">
      <defs>
        <linearGradient id="bv-gold" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.65" />
          <stop offset="100%" stopColor="#D4AF37" stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <line x1="30" y1="150" x2="380" y2="150" stroke="#D4AF37" strokeOpacity="0.2" />
      {/* Without plan (dashed grey) */}
      <path d="M 30 130 L 100 125 L 170 120 L 240 60 L 240 165 L 380 165" stroke="#9CA3AF" strokeWidth="2" fill="none" strokeDasharray="5 5" />
      {/* With plan (gold) */}
      <path d="M 30 130 L 100 115 L 170 95 L 240 70 L 310 45 L 380 25" stroke="#D4AF37" strokeWidth="3" fill="none" strokeLinecap="round" />
      <path d="M 30 130 L 100 115 L 170 95 L 240 70 L 310 45 L 380 25 L 380 150 L 30 150 Z" fill="url(#bv-gold)" />
      {/* "Incident" marker */}
      <circle cx="240" cy="60" r="5" fill="#0A2540" stroke="#D4AF37" strokeWidth="2" />
      <text x="248" y="55" fontFamily="Inter, sans-serif" fontSize="10" fill="#D4AF37">Owner sidelined</text>
      <text x="30" y="170" fontFamily="Inter, sans-serif" fontSize="10" fill="#FAF7F2" opacity="0.6">Year 1</text>
      <text x="355" y="170" fontFamily="Inter, sans-serif" fontSize="10" fill="#FAF7F2" opacity="0.6">Year 10</text>
    </svg>
  );
}
